// Covers asynchronous React handlers invoked by the generic app bridge.
const assistantMutationApprovalApi = (() => {
  let owner: any = null
  const pending = new Set<Promise<any>>()
  const bind = (value: any) => { owner = value; return () => { if (owner === value) owner = null } }
  const request = async (action: string | null, body: any) => {
    const current = owner
    if (!action || !current?.enabled()) return
    const snapshot = JSON.stringify(body)
    const promise = (async () => {
      if (!current.isCurrent() || body.businessId && body.businessId !== current.businessId) throw new Error(t('control.editorChanged'))
      if (!await current.confirm({ action, step: JSON.parse(snapshot) })) throw new Error(t('live.cancelled'))
      if (!current.isCurrent() || snapshot !== JSON.stringify(body)) throw new Error(t('control.editorChanged'))
    })()
    pending.add(promise)
    try { await promise } finally { pending.delete(promise) }
  }
  const httpReason = (endpoint: string, body: any) => {
    const action = String(body.action || '')
    if (/^(delete|remove|resetBusiness|requestBusinessDeletion)/.test(endpoint) || /^(delete|annul|reverse)(_|-|$)/.test(action)) return 'delete'
    if (['sendInvoiceEmailHttp', 'sendCustomerBillingGroupEmailHttp', 'sendPayslipEmailHttp', 'sendAnnualSignatureRequestsHttp', 'sendAnnualSignatureRemindersHttp'].includes(endpoint)) return 'send_invoice'
    if (['createBusinessInviteHttp', 'createBusinessOwnershipTransferInviteHttp', 'acceptBusinessInviteHttp', 'manageEmployeePortalAccessHttp', 'inviteAccountingFirmMemberHttp', 'updateAccountingFirmMemberHttp', 'assignAccountingFirmMemberHttp', 'endAccountingFirmEngagementHttp', 'createAccountingFirmEngagementHttp', 'acceptAccountingFirmEngagementHttp'].includes(endpoint)) return 'manage_members'
    if (endpoint === 'upsertInvoiceHttp' && body.invoice?.status !== 'draft') return 'issue_invoice'
    if (endpoint === 'purchaseMutationHttp' && action === 'upsert' && body.expense?.status !== 'draft') return 'post'
    if (endpoint === 'manualPostingMutationHttp' && ['upsert', 'correct_date', 'correct_entry'].includes(action)) return 'post'
    if (endpoint === 'otherSaleMutationHttp' && action === 'upsert' && body.sale?.status !== 'draft') return 'post'
    if (endpoint === 'yearClosingMutationHttp' && ['close_year', 'reopen_year'].includes(action)) return action
    if (endpoint === 'yearClosingMutationHttp' && action === 'fixed_asset_action') return 'post'
    if (endpoint === 'bankReconciliationMutationHttp' && action === 'book_match_group' || endpoint === 'inventoryCountAdjustmentHttp') return 'post'
    if (endpoint === 'payrollRunMutationHttp' && ['approve', 'reset', 'reverse', 'annul_accounting', 'annul_tax_payment'].includes(action)) return 'approve'
    if (/^(submit|send).*(Vat|TaxReturn|AnnualAccounts|Amelding)|^(initiate|execute).*(Payment|Payout)/.test(endpoint)) return 'submit'
    if (['vatMutationHttp', 'taxReturnMutationHttp', 'annualAccountsMutationHttp'].includes(endpoint) && ['submit', 'send', 'correct_submission'].includes(action)) return 'submit'
    if (['taxReturnMutationHttp', 'annualAccountsMutationHttp'].includes(endpoint) && ['processing', 'done'].includes(body.process?.submissionStage || body.patch?.submissionStage)) return 'submit'
    if (endpoint === 'autopilotExecutionHttp' || endpoint === 'commitAccountingImportJobHttp') return 'post'
    return null
  }
  const beforeHttp = (endpoint: string, body: any) => request(httpReason(endpoint, body), body)
  const drain = async () => { while (pending.size) await Promise.all([...pending]) }
  const wrapBackend = (backend: any) => {
    if (!backend || backend.__assistantDeletionGuard) return
    Object.defineProperty(backend, '__assistantDeletionGuard', { value: true })
    for (const name of Object.keys(backend)) {
      if (typeof backend[name] !== 'function') continue
      const original = backend[name]
      const deletion = /^(delete|remove)/.test(name)
      if (!deletion && original.constructor.name !== 'AsyncFunction') continue
      backend[name] = (...args: any[]) => {
        if (!owner?.enabled()) return original.apply(backend, args)
        // Keep the agent loop alive through uploads and other awaited work before
        // an HTTP mutation starts. Stop can still cancel that mutation at its gate.
        const work = (async () => {
          if (deletion) await request('delete', { operation: name, records: args })
          return original.apply(backend, args)
        })()
        pending.add(work)
        return work.then(value => { pending.delete(work); return value }, error => { pending.delete(work); throw error })
      }
    }
  }
  return { bind, request, beforeHttp, drain, wrapBackend, httpReason, hasPending: () => pending.size > 0 }
})()
Object.assign(globalThis, { PlummeAssistantMutationApproval: assistantMutationApprovalApi })
